// ============================================================
// VidyaTrack — Student Dashboard
// ============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiBook, FiCheckSquare, FiGrid, FiClock, FiTrendingUp } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { format, isPast, isToday, isTomorrow, differenceInDays } from "date-fns";
import StatCard from "../../components/shared/StatCard";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };



function greet() {
  const h = new Date().getHours();


  return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
}

export default function StudentDashboard() {
  const { userData } = useAuth();
  const { getHomework, getSubmissions, getAttendance, getNotices, getGroups, getEvents } = useData();
  const navigate = useNavigate();

  const [hw,       setHw]       = useState([]);
  const [subs,     setSubs]     = useState([]);
  const [att,      setAtt]      = useState([]);
  const [notices,  setNotices]  = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [events,   setEvents]   = useState([]);
  const [loading,  setLoading]  = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      try {
        const [h, s, a, n, g, e] = await Promise.all([
          getHomework({ classId: userData?.classId }),
          getSubmissions({ studentId: userData?.uid }),
          getAttendance({ studentId: userData?.uid }),
          getNotices({ audience: "students" }),
          getGroups({ classId: userData?.classId }),
          getEvents(),
        ]);
        setHw(h); setSubs(s); setAtt(a);
        setNotices(n.slice(0, 4)); setGroups(g); setEvents(e);
      } catch { /* handled silently */ }
      finally { setLoading(false); }
    };
    if (userData?.uid) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid, userData?.classId]);

  const subMap      = new Map(subs.map(s => [s.homeworkId, s]));
  const pendingHw   = hw.filter(h => !subMap.has(h.id) && !isPast(toDate(h.deadline)));
  const overdueHw   = hw.filter(h => !subMap.has(h.id) && isPast(toDate(h.deadline)));
  const presentDays = att.filter(a => a.status === "present").length;
  const attPct      = att.length > 0 ? Math.round((presentDays / att.length) * 100) : 0;
  const hwPct       = hw.length > 0  ? Math.round((subs.length  / hw.length)  * 100) : 0;

  const upcomingEvents = events
    .filter(e => !isPast(toDate(e.date)))
    .sort((a, b) => toDate(a.date) - toDate(b.date))
    .slice(0, 5);

  const deadlineLabel = (dl) => {
    const d = new Date(dl);
    if (isToday(d))    return { text:"Due Today",    color:"var(--red-600)" };
    if (isTomorrow(d)) return { text:"Due Tomorrow", color:"var(--amber-600)" };
    const days = differenceInDays(d, new Date());
    return { text: `${days} days left`, color: days <= 3 ? "var(--amber-600)" : "var(--gray-500)" };
  };

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title">{greet()}, {userData?.name?.split(" ")[0]}! 👋</h1>
          <p className="page-sub">
            {format(new Date(), "EEEE, MMMM d, yyyy")}
            {userData?.className ? ` · ${userData.className}` : ""}
            {userData?.section  ? ` - ${userData.section}`   : ""}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="g g4" style={{ marginBottom:20 }}>
        <StatCard icon={<FiBook/>}       label="Pending Homework"    value={pendingHw.length}  color="var(--blue-700)" bg="var(--blue-100)"  onClick={() => navigate("/student/homework")}/>
        <StatCard icon={<FiCheckSquare/>} label="Attendance Rate"   value={`${attPct}%`}       color="var(--green-600)"    bg="var(--green-50)"  onClick={() => navigate("/student/attendance")}/>
        <StatCard icon={<FiTrendingUp/>} label="HW Completion"      value={`${hwPct}%`}        color="#7c3aed"         bg="#f5f3ff"/>
        <StatCard icon={<FiGrid/>}       label="Groups"             value={groups.length}      color="var(--amber-600)"   bg="var(--amber-50)"  onClick={() => navigate("/student/groups")}/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:16, alignItems:"start" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Pending Homework */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">📚 Pending Homework</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate("/student/homework")}>View All</button>
            </div>
            <div className="card-body">
              {pendingHw.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🎉</div>
                  <div className="empty-title">All caught up!</div>
                  <div className="empty-desc">You have no pending homework. Great work!</div>
                </div>
              ) : pendingHw.slice(0, 5).map(h => {
                const dl = deadlineLabel(h.deadline);
                return (
                  <div key={h.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 0", borderBottom:"1px solid #f0f0f0" }}>
                    <div style={{ width:38, height:38, borderRadius:"var(--r8)", background:"var(--blue-100)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", flexShrink:0 }}>📚</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:".88rem", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{h.title}</div>
                      <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginTop:2 }}>{h.subject} · {h.className}</div>
                    </div>
                    <span style={{ fontSize:".72rem", fontWeight:700, color:dl.color, whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:4 }}>
                      <FiClock size={11}/>{dl.text}
                    </span>
                  </div>
                );
              })}
              {overdueHw.length > 0 && (
                <div style={{ marginTop:10, padding:"8px 12px", background:"var(--red-50)", borderRadius:"var(--r6)", border:"1px solid var(--red-100)", fontSize:".8rem", color:"var(--red-600)", fontWeight:600 }}>
                  ⚠️ {overdueHw.length} overdue assignment{overdueHw.length > 1 ? "s" : ""} — contact your teacher
                </div>
              )}
            </div>
          </div>

          {/* Attendance Summary */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">✅ Attendance Summary</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate("/student/attendance")}>Details</button>
            </div>
            <div className="card-body">
              {att.length === 0 ? (
                <div className="empty-state" style={{ padding:"24px 0" }}>
                  <div className="empty-icon" style={{ fontSize:"2rem" }}>📅</div>
                  <div className="empty-title">No attendance records yet</div>
                </div>
              ) : (
                <>
                  <div className="g g3" style={{ marginBottom:14 }}>
                    {[
                      { label:"Present", count:att.filter(a=>a.status==="present").length, color:"var(--green-600)",   bg:"var(--green-50)" },
                      { label:"Absent",  count:att.filter(a=>a.status==="absent").length,  color:"var(--red-600)",     bg:"var(--red-50)" },
                      { label:"Late",    count:att.filter(a=>a.status==="late").length,    color:"var(--amber-600)",  bg:"var(--amber-50)" },
                    ].map(s => (
                      <div key={s.label} style={{ background:s.bg, borderRadius:"var(--r8)", padding:"12px", textAlign:"center" }}>
                        <div style={{ fontSize:"1.5rem", fontWeight:800, color:s.color, fontFamily:"'Poppins',sans-serif", lineHeight:1 }}>{s.count}</div>
                        <div style={{ fontSize:".72rem", color:"var(--gray-500)", marginTop:4 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5, fontSize:".78rem" }}>
                    <span style={{ color:"var(--gray-500)" }}>Overall Attendance</span>
                    <span style={{ fontWeight:700, color: attPct >= 75 ? "var(--green-600)" : "var(--red-600)" }}>{attPct}%</span>
                  </div>
                  <div className="progress">
                    <div className={`progress-bar ${attPct >= 75 ? "green" : "red"}`} style={{ width:`${attPct}%` }}/>
                  </div>
                  {attPct < 75 && (
                    <div style={{ marginTop:10, padding:"8px 12px", background:"var(--red-50)", borderRadius:"var(--r6)", border:"1px solid var(--red-100)", fontSize:".8rem", color:"var(--red-600)", fontWeight:600 }}>
                      ⚠️ Attendance below 75%. Please attend regularly.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Upcoming Events */}
          <div className="card">
            <div className="card-header"><span className="card-title">📅 Upcoming Events</span></div>
            <div className="card-body" style={{ padding:"10px 16px" }}>
              {upcomingEvents.length === 0 ? (
                <div style={{ padding:"16px 0", textAlign:"center", color:"var(--gray-400)", fontSize:".84rem" }}>No upcoming events</div>
              ) : upcomingEvents.map(e => (
                <div key={e.id} style={{ display:"flex", gap:10, alignItems:"center", padding:"9px 0", borderBottom:"1px solid #f0f0f0" }}>
                  <div style={{ width:38, height:38, borderRadius:"var(--r8)", background:(e.color||"#054bb4")+"18", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <div style={{ fontSize:".78rem", fontWeight:800, color:e.color||"#054bb4", lineHeight:1 }}>{format(toDate(e.date),"d")}</div>
                    <div style={{ fontSize:".58rem", color:e.color||"#054bb4", textTransform:"uppercase" }}>{format(toDate(e.date),"MMM")}</div>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:".84rem", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e.title}</div>
                    <span style={{ fontSize:".68rem", fontWeight:700, padding:"1px 7px", borderRadius:20, background:(e.color||"#054bb4")+"18", color:e.color||"#054bb4" }}>{e.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Notices */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">🔔 Notices</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate("/student/notices")}>All</button>
            </div>
            <div className="card-body" style={{ padding:"10px 16px" }}>
              {notices.length === 0 ? (
                <div style={{ padding:"16px 0", textAlign:"center", color:"var(--gray-400)", fontSize:".84rem" }}>No notices yet</div>
              ) : notices.map(n => (
                <div key={n.id} style={{ padding:"10px 12px", background:n.priority==="high"?"var(--red-50)":"var(--blue-100)", borderRadius:"var(--r6)", borderLeft:`3px solid ${n.priority==="high"?"var(--red-600)":"var(--blue-700)"}`, marginBottom:8 }}>
                  <div style={{ fontSize:".84rem", fontWeight:600 }}>{n.title}</div>
                  <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginTop:3 }}>{n.content?.slice(0,70)}…</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header"><span className="card-title">Quick Access</span></div>
            <div className="card-body" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                { label:"Homework",   icon:"📚", path:"/student/homework"   },
                { label:"Attendance", icon:"✅", path:"/student/attendance" },
                { label:"Materials",  icon:"📖", path:"/student/materials"  },
                { label:"Notices",    icon:"🔔", path:"/student/notices"    },
              ].map(a => (
                <button key={a.path} onClick={() => navigate(a.path)} style={{
                  padding:"12px 8px", background:"var(--gray-100)",
                  border:"1px solid var(--border)", borderRadius:"var(--r8)",
                  cursor:"pointer", display:"flex", flexDirection:"column",
                  alignItems:"center", gap:5,
                  fontFamily:"inherit", transition:"var(--t)",
                }}
                onMouseEnter={e => { e.currentTarget.style.background="var(--blue-100)"; e.currentTarget.style.borderColor="var(--blue-700)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="var(--gray-100)"; e.currentTarget.style.borderColor="var(--border)"; }}
                >
                  <span style={{ fontSize:"1.3rem" }}>{a.icon}</span>
                  <span style={{ fontSize:".74rem", fontWeight:500 }}>{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`@media(max-width:1024px){div[style*="grid-template-columns:1fr 320px"]{grid-template-columns:1fr!important}}`}</style>

    </div>
  );
}